import { useState, useEffect, useCallback, useRef } from 'react'
import type { ImperativePanelHandle } from 'react-resizable-panels'
import { useIsMobile } from './useIsMobile'

const SIDEBAR_STORAGE_KEY = 'caseysPanel.sidebarOpen'

/**
 * Sidebar open/close state with localStorage persistence and keyboard shortcut.
 * Mobile detection is delegated to the shared useIsMobile hook.
 */
export const useSidebar = () => {
  const [showSidebar, setShowSidebar] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY)
    return stored === null ? false : stored === 'true'
  })

  const isMobile = useIsMobile()

  // Ref for imperative panel control (desktop only)
  const panelRef = useRef<ImperativePanelHandle>(null)

  const handlePanelCollapse = useCallback(() => {
    setShowSidebar(false)
    localStorage.setItem(SIDEBAR_STORAGE_KEY, 'false')
  }, [])

  const handlePanelExpand = useCallback(() => {
    setShowSidebar(true)
    localStorage.setItem(SIDEBAR_STORAGE_KEY, 'true')
  }, [])

  const toggleSidebar = useCallback(() => {
    setShowSidebar(prev => {
      const newState = !prev
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(newState))
      return newState
    })
  }, [])

  // Collapse the inline sidebar whenever we are on mobile. useIsMobile only
  // re-renders when the 768px boundary is crossed, so this fires on mount-in-mobile
  // and on the desktop→mobile transition — never fighting a user-opened drawer.
  useEffect(() => {
    if (!isMobile) return
    setShowSidebar(prev => {
      if (prev) localStorage.setItem(SIDEBAR_STORAGE_KEY, 'false')
      return false
    })
  }, [isMobile])

  // Keyboard shortcut (Ctrl+B / Cmd+B)
  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault()
        toggleSidebar()
      }
    }
    window.addEventListener('keydown', handleKeyboard)
    return () => window.removeEventListener('keydown', handleKeyboard)
  }, [toggleSidebar])

  return {
    showSidebar,
    toggleSidebar,
    isMobile,
    panelRef,
    handlePanelCollapse,
    handlePanelExpand,
  }
}
